import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ==============================================
// TYPES EXISTANTS (conservés)
// ==============================================
const EXPORT_PRICE_PER_THOUSAND = 1.00;

export interface ProfessionSetting {
  id: number;
  name: string;
  is_priority: boolean;
  max_free_slots: number | null;
  max_premium_slots: number | null;
  premium_price: number | null;
  export_price: number | null;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ProfessionPricingStats extends ProfessionSetting {
  userCount: number;
  premiumCount: number;
  availableSlots: number;
  occupancyRate: number;
}

export interface PremiumAvailability {
  professionId: number;
  professionName: string;
  maxSlots: number;
  currentPremiumCount: number;
  availableSlots: number;
  nextPosition: number | null;
  price: number;
  isAvailable: boolean;
}

export interface PremiumPurchaseResult {
  success: boolean;
  data: {
    position: number;
    profession: string;
    expires_at: string;
  };
  message: string;
}

// ==============================================
// TYPES - EXPORT PAR PROFESSION (existants)
// ==============================================

export interface ProfessionExportPrice {
  professionId: number;
  professionName: string;
  exportPrice: number;
  isFree: boolean;
}

export interface ExportProfessionInfo {
  id: number;
  name: string;
  exportPrice: number;
  isFree: boolean;
}

// ==============================================
// ✅ NOUVEAUX TYPES - EXPORT PAR VILLE
// ==============================================

export type PricingModel = 'fixed' | 'per_record' | 'per_thousand';


export interface CityExportPrice {
  id: number;
  city_name: string;
  pricing_model: PricingModel;
  fixed_price: number | null;
  price_per_record: number | null;
  is_active: boolean;
  last_updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface CityExportPriceFormData {
  city_name: string;
  pricing_model: PricingModel;
  fixed_price?: number | null;
  price_per_record?: number | null;
  is_active?: boolean;
}

export interface CityExportPriceFormData {
  city_name: string;
  pricing_model: PricingModel;
  fixed_price?: number | null;
  price_per_record?: number | null;
  is_active?: boolean;
}

// ✅ Interface mise à jour avec tous les nouveaux champs
export interface CityExportPriceCalculation {
  available: boolean;
  totalPrice: number;
  pricingModel: PricingModel;
  pricingDetails: {
    model: PricingModel;
    price?: number;
    pricePerRecord?: number;
    pricePerThousand?: number;
    pricePerTranche?: number; 
    recordCount?: number;
    thousands?: number;
    tranches?: number;
    trancheSize?: number; 
    total?: number;
    calculation?: string;
  };
  cityConfig: CityExportPrice | null;
  message?: string;
}

export interface AvailableCityInfo {
  name: string;
  hasConfig: boolean;
  isActive: boolean;
  pricingModel: PricingModel | null;
}

export interface CityExportPurchaseParams {
  paymentIntentId: string;
  amount: number;
  userId: number;
  cityName: string;
  fileName?: string;
  count?: number;
  filters?: any;
}

// ==============================================
// TYPES - ACHATS (PURCHASES) - EXISTANTS
// ==============================================

export type PurchaseType = 'premium' | 'export' | 'credits';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface Purchase {
  id: number;
  user_id: number;
  purchase_type: PurchaseType;
  amount: number;
  currency: string;
  payment_status: PaymentStatus;
  stripe_payment_intent_id: string;
  invoice_number: string;
  invoice_pdf_path: string | null;
  profession_id: number | null;
  profession_name: string | null;
  premium_position: number | null;
  premium_expires_at: string | null;
  export_file_name: string | null;
  export_record_count: number | null;
  export_filters: any | null;
  purchased_at: string;
  created_at: string;
  updated_at: string;
  metadata: any;
  admin_notes: string | null;
  user?: {
    id: number;
    email: string;
    firstname: string;
    name: string;
  };
}

export interface PurchasesResponse {
  success: boolean;
  data: {
    total: number;
    totalAmount: number;
    purchases: Purchase[];
    pagination: {
      limit: number;
      offset: number;
      currentPage: number;
      totalPages: number;
    };
  };
}

export interface PurchaseStats {
  totals: {
    total_purchases: number;
    total_revenue: number;
    unique_customers: number;
  };
  byType: Array<{
    purchase_type: PurchaseType;
    count: number;
    total_amount: number;
    avg_amount: number;
  }>;
  byProfession: Array<{
    profession_name: string;
    count: number;
    total_amount: number;
  }>;
  monthly: Array<{
    month: string;
    count: number;
    total: number;
  }>;
}

// ==============================================
// TYPES - PAIEMENT STRIPE - EXISTANTS
// ==============================================

export interface PaymentIntentResponse {
  success: boolean;
  clientSecret: string;
}

export interface ConfirmPremiumPurchaseParams {
  paymentIntentId: string;
  professionId: number;
  userId: number;
}

export interface ConfirmExportPurchaseParams {
  paymentIntentId: string;
  amount: number;
  userId: number;
  professionName?: string;
  fileName?: string;
  count?: number;
  filters?: any;
  professionId?: number;
}

export interface PremiumPurchaseConfirmResult {
  success: boolean;
  position: number;
  profession: string;
  message: string;
}

// ==============================================
// ✅ NOUVEAU - GESTION DES EXPORTS PAR VILLE
// ==============================================

/**
 * Récupérer toutes les configurations de prix par ville
 */
export const getAllCityPrices = async (params?: {
  activeOnly?: boolean;
  search?: string;
}): Promise<CityExportPrice[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.activeOnly) queryParams.append('activeOnly', String(params.activeOnly));
    if (params?.search) queryParams.append('search', params.search);

    const response = await axios.get(
      `${API_URL}/profession/city-prices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des prix par ville");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des prix par ville.");
    }
  }
};

/**
 * Récupérer le prix d'une ville spécifique
 */
export const getCityPrice = async (cityName: string): Promise<CityExportPrice> => {
  try {
    const response = await axios.get(`${API_URL}/profession/city-prices/${encodeURIComponent(cityName)}`);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération du prix de la ville");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération du prix de la ville.");
    }
  }
};

/**
 * Créer ou mettre à jour le prix d'une ville
 */
export const upsertCityPrice = async (
  cityName: string,
  data: CityExportPriceFormData
): Promise<CityExportPrice> => {
  try {
    const response = await axios.put(
      `${API_URL}/profession/city-prices/${encodeURIComponent(cityName)}`,
      data
    );
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la mise à jour du prix de la ville");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la mise à jour du prix de la ville.");
    }
  }
};

/**
 * Désactiver une ville (soft delete)
 */
export const deactivateCity = async (cityName: string): Promise<CityExportPrice> => {
  try {
    const response = await axios.delete(`${API_URL}/profession/city-prices/${encodeURIComponent(cityName)}`);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la désactivation de la ville");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la désactivation de la ville.");
    }
  }
};

/**
 * Récupérer toutes les villes disponibles depuis l'annuaire
 */
export const getAvailableCities = async (): Promise<AvailableCityInfo[]> => {
  try {
    const response = await axios.get(`${API_URL}/profession/available-cities`);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des villes disponibles");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des villes disponibles.");
    }
  }
};

/**
 * Récupérer la configuration globale d'export
 */
export const getGlobalExportSettings = async (): Promise<GlobalExportSettings> => {
  try {
    const response = await axios.get(`${API_URL}/profession/global-settings`);

    console.log("[DEBUG] getGlobalExportSettings - Response:", response.data);

    if (response.data.success) {
      const data = response.data.data;
      return {
        id: data.id || 0,
        tranche_size: data.tranche_size || 1000,
        price_per_tranche: data.price_per_tranche || 1.00,
        is_active: data.is_active ?? true,
        updated_by: data.updated_by || null,
        created_at: data.created_at || new Date().toISOString(),
        updated_at: data.updated_at || new Date().toISOString(),
        updatedByUser: data.updatedByUser
      };
    } else {
      return {
        id: 0,
        tranche_size: 1000,
        price_per_tranche: 1.00,
        is_active: true,
        updated_by: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }
  } catch (error) {
    console.warn("⚠️ Erreur récupération config, utilisation des valeurs par défaut");
    return {
      id: 0,
      tranche_size: 1000,
      price_per_tranche: 1.00,
      is_active: true,
      updated_by: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
};
/**
 * Calculer le prix d'export pour une ville (utilise la config globale)
 */
export const calculateCityExportPrice = async (
  cityName: string,
  recordCount: number
): Promise<CityExportPriceCalculation> => {
  try {
    // Récupérer la configuration globale
    const settings = await getGlobalExportSettings();
    console.log("[DEBUG] calculateCityExportPrice - Settings:", settings);
    
    const trancheSize = settings.tranche_size;
    const pricePerTranche = settings.price_per_tranche;
    
    // Calcul avec arrondi au supérieur
    const tranches = Math.ceil(recordCount / trancheSize);
    const totalPrice = tranches * pricePerTranche;
    
    console.log(`💰 Calcul export ${cityName}: ${recordCount} contacts = ${tranches} × ${pricePerTranche}€ = ${totalPrice}€`);
    
    return {
      available: true,
      totalPrice,
      pricingModel: 'per_thousand',
      pricingDetails: {
        model: 'per_thousand',
        recordCount,
        trancheSize, 
        tranches, 
        pricePerTranche,
        total: totalPrice,
        calculation: `${recordCount} contacts = ${tranches} tranche(s) de ${trancheSize} à ${pricePerTranche}€ = ${totalPrice}€`
      },
      cityConfig: null,
      message: `Tarification: ${tranches} tranche(s) de ${trancheSize} contacts à ${pricePerTranche}€`
    };
    
  } catch (error: any) {
    console.error("❌ Erreur calculateCityExportPrice:", error);
    return {
      available: false,
      totalPrice: 0,
      pricingModel: 'fixed',
      pricingDetails: {
        model: 'fixed',
        price: 0
      },
      cityConfig: null,
      message: error.message || "Erreur lors du calcul"
    };
  }
};
/**
 * Créer un PaymentIntent pour l'achat d'export par ville
 * (Réutilise la même route que l'export par profession)
 */
export const createCityExportPaymentIntent = async (
  amount: number,
  userId: number | null
): Promise<PaymentIntentResponse> => {
  return createExportPaymentIntent(amount, userId);
};

/**
 * Confirmer l'achat d'export par ville
 */
export const confirmCityExportPurchase = async (
  params: CityExportPurchaseParams
): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/profession/confirm-export-purchase`, {
      paymentIntentId: params.paymentIntentId,
      amount: params.amount,
      userId: params.userId,
      professionName: `Ville: ${params.cityName}`, // On utilise professionName pour stocker la ville
      fileName: params.fileName,
      count: params.count,
      filters: {
        ...params.filters,
        city: params.cityName,
        exportType: 'city'
      },
      metadata: {
        exportType: 'city',
        cityName: params.cityName
      }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

// ==============================================
// FONCTIONS EXISTANTES (conservées)
// ==============================================

// ... [TOUTES LES FONCTIONS EXISTANTES RESTENT ICI] ...

/**
 * Récupérer toutes les professions
 */
export const getAllProfessions = async (): Promise<ProfessionSetting[]> => {
  try {
    const response = await axios.get(`${API_URL}/profession/all`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des professions");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des professions.");
    }
  }
};

/**
 * Mise à jour en masse des professions
 */
export const updateAdminProfessions = async (professions: ProfessionSetting[]) => {
  try {
    const response = await axios.put(`${API_URL}/profession/update`, professions);
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la mise à jour des professions");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la mise à jour des professions.");
    }
  }
};

/**
 * Ajouter une nouvelle profession
 */
export const createAdminProfession = async (profession: {
  name: string;
  is_priority?: boolean;
  max_free_slots?: number | null;
  max_premium_slots?: number | null;
  premium_price?: number | null;
  export_price?: number | null;
}) => {
  try {
    const response = await axios.post(`${API_URL}/profession/create-profession`, profession);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la création de la profession");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la création.");
    }
  }
};

/**
 * Supprimer une profession
 */
export const deleteAdminProfession = async (id: number): Promise<void> => {
  try {
    const response = await axios.delete(`${API_URL}/profession/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Erreur lors de la suppression de la profession");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la suppression de la profession.");
    }
  }
};

/**
 * Récupérer les professions avec leurs statistiques de tarification
 */
export const getProfessionsWithPricing = async (): Promise<ProfessionPricingStats[]> => {
  try {
    const response = await axios.get(`${API_URL}/profession/pricing`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des tarifs");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des tarifs.");
    }
  }
};

/**
 * Mettre à jour le tarif premium ET le prix d'export d'une profession
 */
export const updateProfessionPricing = async (
  id: number,
  data: {
    premium_price?: number | null;
    max_premium_slots?: number | null;
    export_price?: number | null;
  }
) => {
  try {
    const response = await axios.put(`${API_URL}/profession/pricing/${id}`, data);
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la mise à jour des tarifs");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la mise à jour des tarifs.");
    }
  }
};

// ==============================================
// GESTION DES EXPORTS (existantes)
// ==============================================

/**
 * Transformer les données du backend (snake_case) vers le format attendu (camelCase)
 */
const transformExportProfessionData = (data: any): ExportProfessionInfo => {
  return {
    id: data.id,
    name: data.name || data.profession_name || data.professionName,
    exportPrice: data.exportPrice || data.export_price || 0,
    isFree: data.isFree !== undefined ? data.isFree : (data.is_free !== undefined ? data.is_free : false),
  };
};

/**
 * Récupérer toutes les professions avec leur prix d'export
 */
export const getAllExportPrices = async (): Promise<ExportProfessionInfo[]> => {
  try {
    const response = await axios.get(`${API_URL}/profession/export-prices`);
    
    let data: any[] = [];
    
    if (response.data.success && Array.isArray(response.data.data)) {
      data = response.data.data;
    } else if (Array.isArray(response.data.data)) {
      data = response.data.data;
    } else if (Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des prix d'export");
    }

    const transformedData: ExportProfessionInfo[] = data.map((item: any) => 
      transformExportProfessionData(item)
    );

    return transformedData;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des prix d'export.");
    }
  }
};

/**
 * Récupérer le prix d'export d'une profession spécifique
 */
export const getExportPriceByProfession = async (professionId: number): Promise<ProfessionExportPrice> => {
  try {
    const response = await axios.get(`${API_URL}/profession/export-price/${professionId}`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération du prix d'export");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération du prix d'export.");
    }
  }
};

/**
 * Mettre à jour UNIQUEMENT le prix d'export d'une profession
 */
export const updateExportPriceOnly = async (
  professionId: number,
  exportPrice: number | null
): Promise<ProfessionSetting> => {
  try {
    const response = await axios.patch(`${API_URL}/profession/${professionId}/export-price`, {
      export_price: exportPrice
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la mise à jour du prix d'export");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la mise à jour du prix d'export.");
    }
  }
};

// ==============================================
// GESTION DU PREMIUM (existantes)
// ==============================================

/**
 * Vérifier la disponibilité des places premium
 */
export const checkPremiumAvailability = async (professionId: number): Promise<PremiumAvailability> => {
  try {
    const response = await axios.get(`${API_URL}/profession/check-availability/${professionId}`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la vérification des disponibilités");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la vérification des disponibilités.");
    }
  }
};

/**
 * Acheter une position premium (APPELÉ APRÈS PAIEMENT RÉUSSI)
 */
export const purchasePremiumSlot = async (
  professionId: number
): Promise<PremiumPurchaseResult> => {
  try {
    const userId = localStorage.getItem("userId");
    
    const response = await axios.post(`${API_URL}/profession/purchase-premium`, {
      professionId,
      userId: userId ? parseInt(userId, 10) : null
    });
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de l'achat de la position premium");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de l'achat.");
    }
  }
};

/**
 * Récupérer le statut des places premium (admin)
 */
export const getPremiumSlotsStatus = async () => {
  try {
    const response = await axios.get(`${API_URL}/profession/premium-slots-status`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération du statut premium");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération du statut.");
    }
  }
};

/**
 * Libérer manuellement les positions premium expirées (admin)
 */
export const releaseExpiredPremiumSlots = async () => {
  try {
    const response = await axios.post(`${API_URL}/profession/release-expired`);
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la libération des places expirées");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la libération des places.");
    }
  }
};

// ==============================================
// PAIEMENT STRIPE - PREMIUM (existantes)
// ==============================================

/**
 * Créer un PaymentIntent pour l'achat Premium
 */
export const createPremiumPaymentIntent = async (
  amount: number,
  professionId: number,
  userId: number | null
): Promise<PaymentIntentResponse> => {
  try {
    const response = await axios.post(`${API_URL}/profession/create-premium-intent`, {
      amount,
      userId,
      professionId
    });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la création du paiement");
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Confirmer l'achat premium APRÈS paiement réussi
 */
export const confirmPremiumPurchase = async (
  params: ConfirmPremiumPurchaseParams
): Promise<PremiumPurchaseConfirmResult> => {
  try {
    const response = await axios.post(`${API_URL}/profession/confirm-premium-purchase`, {
      paymentIntentId: params.paymentIntentId,
      professionId: params.professionId,
      userId: params.userId
    });

    if (response.data.success) {
      return {
        success: true,
        position: response.data.data?.position || response.data.position,
        profession: response.data.data?.profession || response.data.profession,
        message: response.data.message
      };
    } else {
      throw new Error(response.data.message || "Erreur lors de la confirmation de l'achat");
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

// ==============================================
// PAIEMENT STRIPE - EXPORT (existantes)
// ==============================================

/**
 * Créer un PaymentIntent pour l'achat d'EXPORT
 */
export const createExportPaymentIntent = async (
  amount: number,
  userId: number | null
): Promise<PaymentIntentResponse> => {
  try {
    const response = await axios.post(`${API_URL}/profession/create-export-intent`, {
      amount,
      userId
    });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la création du paiement");
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Confirmer l'achat EXPORT APRÈS paiement réussi
 */
export const confirmExportPurchase = async (
  params: ConfirmExportPurchaseParams
): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/profession/confirm-export-purchase`, {
      paymentIntentId: params.paymentIntentId,
      amount: params.amount,
      userId: params.userId,
      professionName: params.professionName,
      fileName: params.fileName,
      count: params.count,
      filters: params.filters,
      professionId: params.professionId
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Vérifier le statut d'un paiement
 */
export const verifyPayment = async (paymentIntentId: string) => {
  try {
    const response = await axios.get(`${API_URL}/profession/verify-payment/${paymentIntentId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Récupérer la clé publique Stripe
 */
export const getStripePublicKey = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_URL}/profession/stripe-public-key`);
    return response.data.publicKey;
  } catch (error) {
    console.warn("⚠️ Impossible de récupérer la clé publique");
    return process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
  }
};

// ==============================================
// GESTION DES ACHATS (PURCHASES) - existantes
// ==============================================

/**
 * Récupérer l'historique des achats d'un utilisateur
 */
export const getUserPurchases = async (
  userId: number,
  params?: {
    type?: PurchaseType;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }
): Promise<PurchasesResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await axios.get(
      `${API_URL}/profession/user-purchases/${userId}?${queryParams.toString()}`
    );
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des achats");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des achats.");
    }
  }
};

/**
 * Récupérer les détails d'un achat spécifique
 */
export const getPurchaseById = async (purchaseId: number): Promise<Purchase> => {
  try {
    const response = await axios.get(`${API_URL}/profession/purchase/${purchaseId}`);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération de l'achat");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération de l'achat.");
    }
  }
};

/**
 * Télécharger la facture d'un achat
 */
export const downloadInvoice = async (purchaseId: number): Promise<void> => {
  try {
    const response = await axios.get(
      `${API_URL}/profession/purchase/${purchaseId}/invoice`,
      { responseType: 'blob' }
    );
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Essayer d'extraire le nom du fichier depuis l'en-tête Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'facture.pdf';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors du téléchargement de la facture.");
    }
  }
};

// ==============================================
// STATISTIQUES ADMIN - existantes
// ==============================================

/**
 * Récupérer les statistiques des achats (admin)
 */
export const getPurchaseStats = async (
  params?: {
    startDate?: string;
    endDate?: string;
    professionId?: number;
  }
): Promise<PurchaseStats> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.professionId) queryParams.append('professionId', params.professionId.toString());

    const response = await axios.get(
      `${API_URL}/profession/admin/purchase-stats?${queryParams.toString()}`
    );
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la récupération des statistiques");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors de la récupération des statistiques.");
    }
  }
};

/**
 * Rembourser un achat (admin)
 */
export const refundPurchase = async (
  purchaseId: number,
  reason?: string
): Promise<Purchase> => {
  try {
    const response = await axios.post(
      `${API_URL}/profession/admin/refund-purchase/${purchaseId}`,
      { reason }
    );
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Erreur lors du remboursement");
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    } else {
      throw new Error("Une erreur est survenue lors du remboursement.");
    }
  }
};

// ==============================================
// UTILITAIRES - existantes
// ==============================================

/**
 * Vérifier si une profession a un prix d'export payant
 */
export const isExportPaid = (profession: ProfessionSetting): boolean => {
  return !!profession.export_price && profession.export_price > 0;
};

/**
 * Formater le prix d'export pour affichage
 */
export const formatExportPrice = (price: number | null | undefined): string => {
  if (!price || price <= 0) {
    return "Gratuit";
  }
  return `${price} €`;
};

/**
 * Formater une date pour affichage
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

/**
 * Obtenir le libellé du type d'achat
 */
export const getPurchaseTypeLabel = (type: PurchaseType): string => {
  const labels: Record<PurchaseType, string> = {
    premium: 'Place Premium',
    export: 'Export de données',
    credits: 'Achat de crédits'
  };
  return labels[type] || type;
};

/**
 * Obtenir le libellé du statut de paiement
 */
export const getPaymentStatusLabel = (status: PaymentStatus): string => {
  const labels: Record<PaymentStatus, string> = {
    pending: 'En attente',
    completed: 'Complété',
    failed: 'Échoué',
    refunded: 'Remboursé'
  };
  return labels[status] || status;
};

/**
 * Obtenir la couleur du badge de statut
 */
export const getPaymentStatusColor = (status: PaymentStatus): string => {
  const colors: Record<PaymentStatus, string> = {
    pending: 'warning',
    completed: 'success',
    failed: 'error',
    refunded: 'default'
  };
  return colors[status] || 'default';
};

// ==============================================
// ✅ NOUVEAUX UTILITAIRES POUR LES EXPORTS PAR VILLE
// ==============================================

/**
 * Vérifier si une ville a un prix configuré
 */
export const isCityConfigured = (city: AvailableCityInfo): boolean => {
  return city.hasConfig && city.isActive;
};

/**
 * Formater le prix d'une ville pour affichage
 */
export const formatCityPrice = (cityConfig: CityExportPrice): string => {
  if (!cityConfig.is_active) {
    return "Désactivé";
  }
  
  if (cityConfig.pricing_model === 'fixed') {
    return cityConfig.fixed_price ? `${cityConfig.fixed_price} € (fixe)` : "Gratuit";
  } else {
    return cityConfig.price_per_record ? `${cityConfig.price_per_record} € / contact` : "Gratuit";
  }
};

/**
 * Calculer le prix total pour une ville
 */
export const calculateCityTotalPrice = (
  cityConfig: CityExportPrice,
  recordCount: number
): number => {
  if (!cityConfig.is_active) return 0;
  
  if (cityConfig.pricing_model === 'fixed') {
    return cityConfig.fixed_price || 0;
  } else {
    return (cityConfig.price_per_record || 0) * recordCount;
  }
};

/**
 * Obtenir la description du modèle de tarification
 */
/**
 * Obtenir la description du modèle de tarification
 */
export const getPricingModelLabel = (model: PricingModel): string => {
  const labels: Record<PricingModel, string> = {
    fixed: 'Prix fixe',
    per_record: 'Prix par contact',
    per_thousand: 'Prix par millier' 
  };
  return labels[model] || model;
};

export const getCityStats = async (page = 1, limit = 50, search = '') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (search) params.append('search', search);
    
    const response = await axios.get(`${API_URL}/profession/city-stats?${params}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message);
  }
};
// ==============================================
// GESTION DE LA CONFIGURATION GLOBALE DES EXPORTS
// ==============================================

export interface GlobalExportSettings {
  id: number;
  tranche_size: number;
  price_per_tranche: number;
  is_active: boolean;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  updatedByUser?: {
    id: number;
    email: string;
    firstname: string;
    name: string;
  };
}

export interface PriceCalculation {
  recordCount: number;
  trancheSize: number;
  pricePerTranche: number;
  tranches: number;
  totalPrice: number;
  calculation: string;
}


/**
 * Mettre à jour la configuration globale
 */
export const updateGlobalExportSettings = async (
  tranche_size: number,
  price_per_tranche: number
): Promise<GlobalExportSettings> => {
  try {
    const response = await axios.post(`${API_URL}/profession/global-settings`, {
      tranche_size,
      price_per_tranche
    });
    
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || "Erreur lors de la mise à jour");
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw error;
  }
};

/**
 * Calculer un prix pour un nombre de contacts donné
 */
export const calculatePrice = async (recordCount: number): Promise<PriceCalculation> => {
  try {
    const response = await axios.get(`${API_URL}/profession/global-settings/calculate`, {
      params: { recordCount }
    });
    
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || "Erreur lors du calcul");
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw error;
  }
};

/**
 * Récupérer l'historique des configurations
 */
export const getGlobalSettingsHistory = async (limit = 10): Promise<GlobalExportSettings[]> => {
  try {
    const response = await axios.get(`${API_URL}/profession/global-settings/history`, {
      params: { limit }
    });
    
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || "Erreur lors de la récupération de l'historique");
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw error;
  }
};